import { apiClient } from './axios';

import type { AgentBehaviorSnapshot } from '@repo/shared-types';

export type BranchStatus = 'active' | 'staged' | 'merged' | 'abandoned';

export interface ConfigBranch {
  id: string;
  configuration_id: string;
  name: string;
  head_commit_id: string;
  base_commit_id: string;
  status: BranchStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  head?: { id: string; message: string; created_at: string; author_type: string };
}

export interface JsonPatchOp {
  op: 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';
  path: string;
  from?: string;
  value?: unknown;
}

export interface ConfigCommit {
  id: string;
  parent_commit_id: string | null;
  message: string;
  author_type: 'user' | 'harness' | 'system';
  author_id: string | null;
  created_at: string;
  patch: JsonPatchOp[] | null;
}

export interface PromoteResult {
  promotedCommitId: string;
  versionId: string;
}

const base = (configId: string) => `/configurations/${configId}/repo`;

export const configRepositoryApi = {
  branches: async (configId: string) =>
    (await apiClient.get<ConfigBranch[]>(`${base(configId)}/branches`)).data,
  commits: async (configId: string, branch = 'main', limit = 50) =>
    (
      await apiClient.get<ConfigCommit[]>(`${base(configId)}/commits`, {
        params: { branch, limit },
      })
    ).data,
  snapshot: async (configId: string, commitId: string) =>
    (await apiClient.get<AgentBehaviorSnapshot>(`${base(configId)}/commits/${commitId}`)).data,
  diff: async (configId: string, from: string, to: string) =>
    (await apiClient.get<JsonPatchOp[]>(`${base(configId)}/diff`, { params: { from, to } })).data,
  createBranch: async (configId: string, name: string, fromCommitId?: string) =>
    (
      await apiClient.post<ConfigBranch>(`${base(configId)}/branches`, {
        name,
        from_commit_id: fromCommitId,
      })
    ).data,
  promote: async (configId: string, branchId: string) =>
    (await apiClient.post<PromoteResult>(`${base(configId)}/branches/${branchId}/promote`)).data,
  abandon: async (configId: string, branchId: string) =>
    (await apiClient.post<ConfigBranch>(`${base(configId)}/branches/${branchId}/abandon`)).data,
  rollback: async (configId: string, toCommitId: string) =>
    (
      await apiClient.post<PromoteResult>(`${base(configId)}/rollback`, {
        to_commit_id: toCommitId,
      })
    ).data,
};
